import { mulberry32, seedFrom, shuffle } from "./random.js";
/**
 * Find them all — a field of arrows, and every one pointing the same way as the sign.
 *
 * The first activity here whose interesting behaviour is *coverage*. Every other game asks a child
 * to find ONE thing and stops the moment they do; this one is not finished until the whole field
 * has been looked at, so a child who scans row by row and a child who jumps about produce visibly
 * different trials. Spot-the-difference is the nearest thing to it and is a comparison between two
 * small panels rather than a sweep of one large one.
 *
 * **Arrows rather than pictures, and the reason is that a direction has no name a child can hold.**
 * Searching a field of apples and fish is a search for a THING, and a five-year-old does that by
 * remembering the word. An arrow pointing north-east is not a word; it can only be held as an
 * orientation and compared to the sign at the top, which is what makes the eight-direction levels
 * genuinely harder than the four-direction ones rather than merely busier.
 *
 * **The field never exceeds four columns.** Four cells across a 360 px phone come out at about
 * 77 px, which is the floor at which a five-year-old's finger lands where they aimed. Difficulty
 * grows down the screen and then through the directions on offer, never sideways.
 */
/** Eight directions, clockwise from up. The renderer turns these into angles; nothing else unpacks. */
export const SEARCH_DIRECTIONS = 8;
/** The four square directions, which is all the lower levels use. */
const SQUARE_DIRS = [0, 2, 4, 6];
const ALL_DIRS = [0, 1, 2, 3, 4, 5, 6, 7];
export const SEARCH_MIN_DIFFICULTY = 1;
export const SEARCH_MAX_DIFFICULTY = 8;
/**
 * Eight levels: a bigger field, then more directions, then closer ones.
 *
 * The field stops growing at 4×5. Twenty cells is already a screenful at the touch floor, and past
 * it the level would be measuring patience: a child who can find six arrows among twenty can find
 * six among forty, given long enough, and how long they are willing to spend is not what this is
 * for.
 */
function recipeFor(difficulty) {
    switch (difficulty) {
        /**
         * Six arrows, two of them right, and only two directions on the board.
         *
         * **The first level opens small on purpose.** It used to be a nine-cell field with three
         * targets, which is not a hard puzzle and is a busy first screen — and this game asks something
         * no other game here asks, that the child keep going after finding one. Learning that rule and
         * sweeping a full board are two things, and a first level should only ever teach one. Six cells
         * is a board a five-year-old takes in whole, so the only thing left to notice is that the
         * counter at the bottom is not full yet.
         */
        case 1:
            return { cols: 2, rows: 3, directions: 2, targetCount: 2, nearMiss: false };
        case 2:
            return { cols: 3, rows: 3, directions: 2, targetCount: 3, nearMiss: false };
        case 3:
            return { cols: 3, rows: 3, directions: 4, targetCount: 3, nearMiss: false };
        case 4:
            return { cols: 3, rows: 4, directions: 4, targetCount: 4, nearMiss: false };
        case 5:
            return { cols: 4, rows: 4, directions: 4, targetCount: 4, nearMiss: false };
        // The diagonals arrive: now an arrow can be wrong by a quarter turn instead of a half.
        case 6:
            return { cols: 4, rows: 4, directions: 8, targetCount: 5, nearMiss: false };
        case 7:
            return { cols: 4, rows: 5, directions: 8, targetCount: 6, nearMiss: false };
        default:
            return { cols: 4, rows: 5, directions: 8, targetCount: 6, nearMiss: true };
    }
}
/**
 * Wrong taps allowed before the trial closes.
 *
 * Scaled to the FIELD rather than to the number of targets, because what it is forgiving is
 * searching: a child working through twenty cells has more places to be wrong than one working
 * through nine, and the same allowance would be generous in the first case and punitive in the
 * second. The budget exists so a trial can end — without one, tapping every cell solves any trial
 * and the score stops meaning anything.
 */
export function searchWrongTapBudget(stimulus) {
    return Math.ceil((stimulus.cols * stimulus.rows) / 6) + 2;
}
export function generateSearch(task, formId, trialIndex, difficulty) {
    if (difficulty < SEARCH_MIN_DIFFICULTY || difficulty > SEARCH_MAX_DIFFICULTY) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const { cols, rows, directions, targetCount, nearMiss } = recipeFor(difficulty);
    const cells = cols * rows;
    if (targetCount >= cells) {
        throw new Error(`difficulty ${difficulty} asks for ${targetCount} targets in ${cells} cells`);
    }
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    /**
     * The direction hunted for is drawn per trial, never fixed.
     *
     * With a fixed target the game would be "tap the ones pointing up", which a child can play
     * without ever reading the sign — and the sign is the only thing that makes this a comparison
     * rather than a rule they memorised on their first sitting.
     */
    const palette = directions === 8 ? ALL_DIRS : SQUARE_DIRS;
    const target = palette[Math.floor(rand() * palette.length)];
    /** What a wrong arrow may be. */
    const wrongPool = directions === 2
        ? [(target + 4) % SEARCH_DIRECTIONS]
        : palette.filter((d) => d !== target);
    /** The two arrows a quarter turn away, which is as close as a wrong one can get. */
    const neighbours = [
        (target + 1) % SEARCH_DIRECTIONS,
        (target + SEARCH_DIRECTIONS - 1) % SEARCH_DIRECTIONS,
    ];
    // Drawn without replacement, so the trial holds exactly as many targets as the level claims.
    const targets = shuffle(Array.from({ length: cells }, (_, i) => i), rand)
        .slice(0, targetCount)
        .sort((a, b) => a - b);
    const targetSet = new Set(targets);
    const field = [];
    for (let cell = 0; cell < cells; cell++) {
        if (targetSet.has(cell)) {
            field.push(target);
            continue;
        }
        // Near-miss levels lean on the neighbours without excluding the rest: a field of nothing but
        // near misses reads as one texture, and the occasional obvious arrow is what keeps it a search.
        const pool = nearMiss && rand() < 0.7 ? neighbours : wrongPool;
        field.push(pool[Math.floor(rand() * pool.length)]);
    }
    return { cols, rows, target, cells: field, targets };
}
/** Found every one, without exhausting the wrong-tap budget. */
export function scoreSearchTrial(stimulus, found, wrongTaps) {
    const need = new Set(stimulus.targets);
    for (const cell of found)
        need.delete(cell);
    return need.size === 0 && wrongTaps <= searchWrongTapBudget(stimulus);
}
//# sourceMappingURL=search.js.map